import type { ErrorEvent } from "@sentry/nextjs";

/**
 * Stack frames that only exist inside a headless scraper's runtime, never a
 * real browser.
 *
 * Obscura is a Deno-based headless crawler that loads our pages and runs the
 * bundle against its own partial DOM. Its <link> elements have no `rel`, so
 * React's hydration pass (`node.rel.toLowerCase()` while it clears stray
 * <head> children) throws inside React itself. The stack gives it away: the
 * bottom frames are `<obscura:bootstrap>` and Deno's `ext:core/01_core.js`,
 * which no visitor's Chrome, Safari or Firefox ever has.
 *
 * Nothing to fix on our side, and no person saw it. Matching on the runtime's
 * own frames (not the message) keeps the same TypeError from a real browser
 * reporting as normal.
 */
const HEADLESS_RUNTIME_FRAME = /^<obscura:|^ext:(core|deno_)/;

export function isHeadlessRuntimeEvent(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some((value) =>
    (value.stacktrace?.frames ?? []).some((frame) =>
      [frame.filename, frame.abs_path].some(
        (path) => typeof path === "string" && HEADLESS_RUNTIME_FRAME.test(path),
      ),
    ),
  );
}
